import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Dropdown, List, Typography } from 'antd';
import { BellOutlined, CheckOutlined, LinkOutlined } from '@ant-design/icons';
import type { Notification } from '@lens/shared';
import { listNotifications, markNotificationsRead } from './api.ts';
import { useNotifications } from './NotificationsContext.tsx';

const { Text, Link } = Typography;

const KIND_LABELS: Record<string, string> = {
  mention: 'Упоминание',
  assignment: 'Назначение',
  reply: 'Ответ',
};

export function NotificationBell({ onRead }: { onRead?: () => void } = {}): JSX.Element {
  const navigate = useNavigate();
  // Список (вместе с прочитанными) нужен только этому дропдауну — держим тут.
  // А счётчик берём из общего провайдера: иначе цифра расходится с подсветкой
  // записей на доске, которая гаснет сразу при открытии записи.
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { unreadCount, reload: reloadUnread } = useNotifications();
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await listNotifications(false);
      setNotifications(res.notifications);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    void load();
    // Poll every 60 seconds
    const interval = setInterval(() => void load(), 60_000);
    return () => clearInterval(interval);
  }, [load]);

  // Подсветку сняли где-то ещё (открыли запись на доске) — подтянуть список,
  // чтобы дропдаун не показывал прочитанное как непрочитанное.
  useEffect(() => {
    void load();
  }, [unreadCount, load]);

  const handleMarkAllRead = async () => {
    try {
      await markNotificationsRead({ all: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, readAt: new Date().toISOString() })));
      reloadUnread();
      onRead?.();
    } catch {
      // ignore
    }
  };

  const handleClickNotification = async (n: Notification) => {
    // Mark as read
    if (!n.readAt) {
      try {
        await markNotificationsRead({ ids: [n.id] });
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === n.id ? { ...item, readAt: new Date().toISOString() } : item,
          ),
        );
        reloadUnread();
        onRead?.();
      } catch {
        // ignore
      }
    }

    // Navigate to the item (deep-link opens its drawer) or, failing that, the space
    const payload = n.payload as Record<string, unknown>;
    if (payload.spaceId) {
      const base = `/spaces/${String(payload.spaceId)}`;
      navigate(payload.itemId ? `${base}?item=${String(payload.itemId)}` : base);
      setOpen(false);
    }
  };

  const menu = open ? (
    <div
      style={{
        background: 'var(--color-bg-container)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        width: 380,
        maxHeight: 420,
        overflow: 'auto',
      }}
      data-testid="notification-dropdown"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px 8px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Text strong>Уведомления</Text>
        {unreadCount > 0 && (
          <Button
            type="text"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => void handleMarkAllRead()}
          >
            Прочитать все
          </Button>
        )}
      </div>
      {notifications.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>Нет уведомлений</div>
      ) : (
        <List
          size="small"
          dataSource={notifications.slice(0, 20)}
          renderItem={(n) => {
            const payload = n.payload as Record<string, unknown>;
            const isAssignment = n.kind === 'assignment';
            const actor = (isAssignment ? payload.assignedByName : payload.mentionedByName) as
              | string
              | undefined;
            const fieldName = payload.fieldName as string | undefined;
            // Через что позвали: название группы или «все». Пусто — позвали лично.
            const mentionedVia = payload.mentionedVia as string | undefined;
            const snippet = (payload.snippet as string) ?? '';
            const itemTitle = payload.itemTitle as string | undefined;
            const displayKey = payload.displayKey as string | undefined;
            const itemTypeName = payload.itemTypeName as string | undefined;
            const spaceName = payload.spaceName as string | undefined;
            const hasItem = Boolean(payload.itemId);
            const cardLabel = itemTitle || displayKey || 'Карточка';

            return (
              <List.Item
                key={n.id}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  background: n.readAt ? 'transparent' : '#f0f5ff',
                }}
                onClick={() => void handleClickNotification(n)}
                data-testid={`notification-${n.id}`}
              >
                <List.Item.Meta
                  title={
                    isAssignment ? (
                      <Text style={{ fontSize: 13 }}>
                        <Text strong>{actor ?? 'Кто-то'}</Text>
                        {' указал(а) вас в поле '}
                        {fieldName ? <Text strong>«{fieldName}»</Text> : 'карточки'}
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                          · {KIND_LABELS[n.kind] ?? n.kind}
                        </Text>
                      </Text>
                    ) : n.kind === 'reply' ? (
                      <Text style={{ fontSize: 13 }}>
                        <Text strong>{actor ?? 'Кто-то'}</Text>
                        {' ответил(а) на ваш комментарий'}
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                          · {KIND_LABELS[n.kind] ?? n.kind}
                        </Text>
                      </Text>
                    ) : (
                    <Text style={{ fontSize: 13 }}>
                      <Text strong>{actor ?? 'Кто-то'}</Text>
                      {mentionedVia === 'все'
                        ? ' упомянул(а) всех участников'
                        : mentionedVia
                          ? ' упомянул(а) группу '
                          : ' упомянул(а) вас'}
                      {mentionedVia && mentionedVia !== 'все' ? (
                        <Text strong>«{mentionedVia}»</Text>
                      ) : null}
                      {payload.source === 'whiteboard'
                        ? ' на доске записи'
                        : hasItem
                          ? ' в комментарии к записи'
                          : ' в комментарии'}
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                        · {KIND_LABELS[n.kind] ?? n.kind}
                      </Text>
                    </Text>
                    )
                  }
                  description={
                    <div>
                      {hasItem && (
                        <div style={{ marginBottom: 4 }}>
                          <LinkOutlined style={{ fontSize: 11, marginRight: 4, color: '#1677ff' }} />
                          <Link
                            style={{ fontSize: 12 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleClickNotification(n);
                            }}
                          >
                            {displayKey ? `${displayKey} · ` : ''}
                            {cardLabel}
                          </Link>
                          {itemTypeName ? (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {' · '}
                              {itemTypeName}
                            </Text>
                          ) : null}
                        </div>
                      )}
                      {snippet ? (
                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                          «{snippet}»
                        </Text>
                      ) : null}
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {spaceName ? `${spaceName} · ` : ''}
                        {new Date(n.createdAt).toLocaleString('ru-RU')}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </div>
  ) : <div />;

  return (
    <Dropdown
      dropdownRender={() => menu}
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          data-testid="notification-bell"
        />
      </Badge>
    </Dropdown>
  );
}
